import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fileUrl } from "@/api";
import { Card, Btn, Spinner, StatusBadge } from "@/components/ui-kit";
import { AffiliateBadge } from "@/components/AffiliateCard";
import { EmptyState, ProBadge } from "@/components/dashboard-bits";
import { MapPin, Calendar, User, Info } from "lucide-react";

function formatDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("fi-FI", { day: "numeric", month: "long", year: "numeric" }); }
  catch { return ""; }
}

function ApplicantCard({ a, onOpen }) {
  const c = a.creator || {};
  const p = c.profile || {};
  const photo = p.profile_photo || c.picture;
  const initials = (c.name || a.creator_name || "?").slice(0, 2).toUpperCase();
  const isPro = p.is_pro || c.is_pro;
  const unread = !a.read;

  return (
    <Card className={`p-5 cursor-pointer transition-shadow duration-300 hover:soft-shadow-hover${unread ? " ring-1 ring-[#7EA8E6] bg-[#F6F8FB]" : ""}`} onClick={() => onOpen(a)} data-testid="received-application-card">
      <div className="flex gap-4">
        <div className="h-14 w-14 rounded-full bg-[#E9EAEC] flex items-center justify-center overflow-hidden font-medium text-[#292827] shrink-0">
          {photo ? <img src={fileUrl(photo)} alt="" className="h-full w-full object-cover" /> : initials}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
            <div className="flex items-center gap-2 min-w-0">
              {unread && <span className="h-2 w-2 rounded-full bg-[#5B7FB0] shrink-0" data-testid="application-unread-dot" />}
              <h4 className={`text-[#292827] tracking-tightish truncate ${unread ? "font-bold" : "font-semibold"}`}>{c.name || a.creator_name}</h4>
              {isPro && <ProBadge testid="applicant-pro-badge" />}
              {a.campaign_kind === "affiliate" && <StatusBadge status={a.status} />}
              {unread && <span className="rounded-full bg-[#DCE6F5] px-2 py-0.5 text-[11px] font-medium text-[#292827] shrink-0" data-testid="application-unread-badge">Uusi</span>}
            </div>
            <span className="flex items-center gap-1.5 text-xs text-[#968F85] shrink-0" data-testid="application-date">
              <Calendar size={13} /> {formatDate(a.created_at)}
            </span>
          </div>
          {p.location && <p className="mt-1 text-sm text-[#6E675C] flex items-center gap-1"><MapPin size={13} className="text-[#968F85]" /> {p.location}</p>}
          {a.message && <p className="mt-2 text-sm text-[#6E675C] leading-relaxed line-clamp-2">{a.message}</p>}
          <div className="mt-4">
            <Btn variant="sm" onClick={(e) => { e.stopPropagation(); onOpen(a); }} data-testid="view-application">
              <User size={15} /> Näytä hakemus
            </Btn>
          </div>
        </div>
      </div>
    </Card>
  );
}

const SCROLL_KEY = "bizAppsScroll";

export default function BusinessApplications() {
  const [apps, setApps] = useState(null);
  const [camps, setCamps] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    Promise.all([api.get("/applications/received"), api.get("/campaigns/mine").catch(() => ({ data: [] }))])
      .then(([a, c]) => { setCamps(c.data || []); setApps(a.data); });
  }, []);

  useEffect(() => {
    if (!apps) return;
    const saved = sessionStorage.getItem(SCROLL_KEY);
    if (saved) requestAnimationFrame(() => window.scrollTo(0, parseInt(saved, 10) || 0));
  }, [apps]);

  const open = (a) => {
    sessionStorage.setItem(SCROLL_KEY, String(window.scrollY));
    navigate(`/dashboard/saapuneet-hakemukset/${a.application_id}`);
  };

  if (!apps) return <Spinner />;

  const groups = [];
  const index = {};
  for (const c of camps) { index[c.campaign_id] = groups.length; groups.push({ id: c.campaign_id, title: c.title, affiliate: c.kind === "affiliate", items: [] }); }
  for (const a of apps) {
    const key = a.campaign_id || a.campaign_title || "muut";
    if (!(key in index)) { index[key] = groups.length; groups.push({ id: a.campaign_id, title: a.campaign_title || "Kampanja", affiliate: a.campaign_kind === "affiliate", items: [] }); }
    groups[index[key]].items.push(a);
  }
  const sections = [
    { title: "Kampanjat", testid: "applications-section-campaigns", groups: groups.filter((g) => !g.affiliate) },
    { title: "Affiliate-ohjelmat", testid: "applications-section-affiliates", groups: groups.filter((g) => g.affiliate) },
  ].filter((s) => s.groups.length);

  return (
    <div data-testid="business-applications-page">
      <h3 className="text-2xl font-semibold tracking-display text-[#292827] mb-6">Saapuneet hakemukset</h3>

      <div className="mb-8 flex gap-3 rounded-2xl bg-[#E9EAEC] border border-[#E6E1D9] px-5 py-4" data-testid="business-applications-info">
        <Info size={18} className="text-[#292827] shrink-0 mt-0.5" strokeWidth={1.75} />
        <p className="text-sm text-[#3A3733] leading-relaxed">
          Kun löydät sopivan hakijan, ota häneen yhteyttä Creatorin kautta. Yhteistyön lopulliset ehdot, sopimukset ja laskutuksen sovitte keskenänne. Creatori toimii yhteistyökumppaneiden löytämisen alustana.
        </p>
      </div>

      {groups.length === 0 ? (
        <EmptyState title="Ei hakemuksia vielä" subtitle="Hakemukset kampanjoihisi näkyvät täällä." />
      ) : (
        <div className="space-y-14">
          {sections.map((s) => (
            <div key={s.title} data-testid={s.testid}>
              <h3 className="text-xs font-semibold tracking-[0.14em] uppercase text-[#968F85] mb-5">{s.title}</h3>
              <div className="space-y-10">
                {s.groups.map((g, gi) => (
                  <section key={gi} data-testid="campaign-applications-group">
                    <div className="flex flex-wrap items-center gap-2 mb-4">
                      {g.affiliate && <AffiliateBadge c={{ campaign_id: g.id }} />}
                      <h4 className="text-[17px] font-semibold tracking-tightish text-[#292827]" data-testid="applications-campaign-name">{g.title}</h4>
                      <span className="text-sm text-[#968F85]">{g.items.length} hakemusta</span>
                    </div>
                    {g.items.length === 0 ? (
                      <p className="rounded-2xl border border-dashed border-[#E6E1D9] px-5 py-4 text-sm text-[#968F85]" data-testid="applications-group-empty">Tähän {g.affiliate ? "ohjelmaan" : "kampanjaan"} ei ole vielä tullut hakemuksia.</p>
                    ) : (
                      <div className="space-y-4">
                        {g.items.map((a) => <ApplicantCard key={a.application_id} a={a} onOpen={open} />)}
                      </div>
                    )}
                  </section>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
